import type { RGB } from '../types';
import { rgbToHsl as rgbToHslRaw, hslToRgb as hslToRgbRaw } from './colors';

// Wrapper: colors.ts rgbToHsl takes (r, g, b) and returns { h: 0-360, s: 0-100, l: 0-100 }
const rgbToHsl = ({ r, g, b }: RGB) => rgbToHslRaw(r, g, b);

// Wrapper: colors.ts hslToRgb takes (h: 0-360, s: 0-100, l: 0-100) and returns RGB
const hslToRgb = ({ h, s, l }: { h: number; s: number; l: number }): RGB => hslToRgbRaw(h, s, l);

export interface ColorSuggestion {
  name: string;
  color: RGB;
}

export const getComplementaryColor = (rgb: RGB): RGB => {
  const hsl = rgbToHsl(rgb);
  // Shift hue by 180 degrees
  let newH = (hsl.h + 180) % 360;
  if (newH < 0) newH += 360;
  return hslToRgb({ h: newH, s: hsl.s, l: hsl.l });
};

export const getHarmonies = (rgb: RGB): ColorSuggestion[] => {
  const hsl = rgbToHsl(rgb);
  
  const createColor = (hOffset: number, sMult: number, lMult: number): RGB => {
    let newH = (hsl.h + hOffset) % 360;
    if (newH < 0) newH += 360;

    // Clamp saturation and lightness (0-100 scale)
    let newS = Math.max(0, Math.min(100, hsl.s * sMult));
    let newL = Math.max(0, Math.min(100, hsl.l * lMult));

    // Fix: Avoid generating identical white/black duplicates if already extreme
    if (lMult !== 1) {
       if (hsl.l > 90) newL = 80; // If too bright, darken
       else if (hsl.l < 10) newL = 20; // If too dark, lighten
    }

    return hslToRgb({ h: newH, s: newS, l: newL });
  };

  return [
    { name: 'Analogous', color: createColor(30, 1, 1) },
    { name: 'Triadic', color: createColor(120, 1, 1) },
    { name: 'Split Comp.', color: createColor(150, 1, 1) },
  ];
};